import { useCallback, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { serversApi } from '../api'
import type { ServerLogsParams } from '../api'
import { useServerStatusHub, useServerLogsHub } from '../hooks/useSignalR'
import { useAuthStore } from '../store/authStore'
import type { EdgeServer, MachineLog, MachineLogType, ServerLiveStatus, ServerLogsPage } from '../types'
import ServerCard, { type CardStatus } from '../components/ServerCard'
import { LampBoard } from '../components/serverStatus'
import { LAMP_TEXT, serverLamp } from '../components/serverLamp'
import { LampLabel, StackMark } from '../components/Lamp'
import ThemeToggle from '../components/ThemeToggle'
import { TypeBadge } from './serverDetail/ui'
import { inputCls, primaryBtn } from '../components/form'

function serverFlatStatus(server: EdgeServer): CardStatus | undefined {
  return server.cpuUsage != null
    ? { cpuUsage: server.cpuUsage, memoryUsage: server.memoryUsage!, diskUsage: server.diskUsage! }
    : undefined
}

// ── 서버 상세 모달 (실시간/조회) — 페이지 이동 없이 뷰어 화면 안에서만 보여줌 ──

const LOG_TYPES: MachineLogType[] = ['Alarm', 'Parameter', 'Mete', 'Signal']
const TYPE_LABELS: Record<MachineLogType, string> = { Alarm: 'ALARM', Parameter: 'PARAMETER', Mete: 'METE', Signal: 'SIGNAL' }
const MAX_LIVE = 300

function formatTime(iso: string) {
  return new Date(iso).toLocaleString('ko-KR', { hour12: false })
}

function LogTable({ logs, empty }: { logs: MachineLog[]; empty: string }) {
  return (
    <div className="bg-surface rounded-xl border border-gray-200 overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-gray-50 text-gray-500 text-xs">
          <tr>
            <th className="text-left px-4 py-3 w-48">시각</th>
            <th className="text-left px-4 py-3 w-28">종류</th>
            <th className="text-left px-4 py-3">설비</th>
            <th className="text-left px-4 py-3">코드</th>
            <th className="text-left px-4 py-3">값</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-50">
          {logs.length === 0 ? (
            <tr><td colSpan={5} className="px-4 py-8 text-center text-gray-400 text-sm">{empty}</td></tr>
          ) : (
            logs.map(log => (
              <tr key={log.id} className="hover:bg-gray-50">
                <td className="px-4 py-2.5 text-gray-500 text-xs font-mono whitespace-nowrap">{formatTime(log.collectedAt)}</td>
                <td className="px-4 py-2.5">
                  <TypeBadge type={log.type} />
                </td>
                <td className="px-4 py-2.5 font-medium text-gray-800">{log.machineCode}</td>
                <td className="px-4 py-2.5 text-gray-600 font-mono text-xs">{log.code}</td>
                <td className="px-4 py-2.5 text-gray-700">{log.value}</td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  )
}

function LiveTab({ serverId }: { serverId: string }) {
  const [buffer, setBuffer] = useState<MachineLog[]>([])
  const onLogs = useCallback((logs: MachineLog[]) => {
    setBuffer(prev => [...logs].reverse().concat(prev).slice(0, MAX_LIVE))
  }, [])
  useServerLogsHub(serverId, onLogs)

  return (
    <div>
      <div className="text-xs text-gray-400 mb-3">실시간 수신 {buffer.length}건 (최대 {MAX_LIVE})</div>
      <LogTable logs={buffer} empty="아직 수신된 로그가 없습니다." />
    </div>
  )
}

interface QueryForm { from: string; to: string; type: '' | MachineLogType; machineCode: string; code: string }
const EMPTY_FORM: QueryForm = { from: '', to: '', type: '', machineCode: '', code: '' }

function QueryTab({ serverId }: { serverId: string }) {
  const [form, setForm] = useState<QueryForm>(EMPTY_FORM)
  const [applied, setApplied] = useState<QueryForm | null>(null)
  const [pages, setPages] = useState<MachineLog[]>([])
  const [cursor, setCursor] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  const buildParams = (f: QueryForm, nextCursor?: string): ServerLogsParams => {
    const params: ServerLogsParams = {}
    if (f.type) params.type = f.type
    if (f.machineCode) params.machineCode = f.machineCode
    if (f.code) params.code = f.code
    if (f.from) params.from = new Date(f.from).toISOString()
    if (f.to) params.to = new Date(f.to).toISOString()
    if (nextCursor) params.cursor = nextCursor
    return params
  }

  const load = async (f: QueryForm, nextCursor?: string) => {
    setLoading(true)
    try {
      const { data } = await serversApi.getServerLogs(serverId, buildParams(f, nextCursor))
      const page = data as ServerLogsPage
      setPages(prev => (nextCursor ? [...prev, ...page.items] : page.items))
      setCursor(page.nextCursor)
    } finally {
      setLoading(false)
    }
  }

  const onSearch = () => { setApplied(form); setPages([]); setCursor(null); load(form) }
  const onMore = () => { if (applied && cursor) load(applied, cursor) }

  return (
    <div>
      <div className="bg-surface rounded-xl border border-gray-200 p-4 mb-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
          <div>
            <label className="block text-xs text-gray-500 mb-1">시작</label>
            <input type="datetime-local" value={form.from} onChange={e => setForm({ ...form, from: e.target.value })}
              className={inputCls} />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">종료</label>
            <input type="datetime-local" value={form.to} onChange={e => setForm({ ...form, to: e.target.value })}
              className={inputCls} />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">종류</label>
            <select value={form.type} onChange={e => setForm({ ...form, type: e.target.value as QueryForm['type'] })}
              className={inputCls}>
              <option value="">전체</option>
              {LOG_TYPES.map(t => <option key={t} value={t}>{TYPE_LABELS[t]}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">설비코드</label>
            <input value={form.machineCode} onChange={e => setForm({ ...form, machineCode: e.target.value })}
              className={inputCls} />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">코드</label>
            <input value={form.code} onChange={e => setForm({ ...form, code: e.target.value })}
              className={inputCls} />
          </div>
        </div>
        <div className="flex justify-end mt-4">
          <button onClick={onSearch} disabled={loading}
            className={primaryBtn}>
            조회
          </button>
        </div>
      </div>

      {applied ? (
        <>
          <LogTable logs={pages} empty="조회 결과가 없습니다." />
          <div className="flex items-center justify-center mt-4">
            {cursor ? (
              <button onClick={onMore} disabled={loading}
                className="border border-gray-300 text-gray-600 px-5 py-2 rounded-lg text-sm hover:bg-gray-50 transition disabled:opacity-50">
                {loading ? '불러오는 중…' : '더 보기'}
              </button>
            ) : pages.length > 0 ? (
              <span className="text-xs text-gray-400">마지막까지 조회했습니다 (총 {pages.length}건)</span>
            ) : null}
          </div>
        </>
      ) : (
        <div className="bg-surface rounded-xl border border-gray-200 p-8 text-center text-gray-400 text-sm">
          조건을 설정하고 조회하세요.
        </div>
      )}
    </div>
  )
}

function ServerDetailModal({ server, onClose }: { server: EdgeServer; onClose: () => void }) {
  const [tab, setTab] = useState<'live' | 'query'>('live')

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50" onClick={onClose}>
      <div
        className="bg-canvas rounded-xl shadow-xl ring-1 ring-gray-200 w-full max-w-4xl max-h-[85vh] overflow-y-auto p-6"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-4">
          <div>
            <h2 className="text-lg font-bold text-gray-800">{server.name}</h2>
            <p className="text-xs text-gray-400 mt-1">{server.ipAddress ?? '-'}</p>
          </div>
          <div className="flex items-center gap-3">
            <LampLabel state={serverLamp(server)}>{LAMP_TEXT[serverLamp(server)]}</LampLabel>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-sm">닫기</button>
          </div>
        </div>

        <div className="border-b border-gray-200 mb-6">
          <div className="flex gap-6">
            {(['live', 'query'] as const).map(t => (
              <button key={t} onClick={() => setTab(t)}
                className={`pb-3 text-sm font-medium transition border-b-2 ${
                  tab === t ? 'border-accent text-gray-900' : 'border-transparent text-gray-500 hover:text-gray-800'
                }`}>
                {t === 'live' ? '실시간' : '조회'}
              </button>
            ))}
          </div>
        </div>

        {tab === 'live' ? <LiveTab serverId={server.id} /> : <QueryTab serverId={server.id} />}
      </div>
    </div>
  )
}

// ── 뷰어 대시보드 ──

export default function ViewerDashboard() {
  const { logout } = useAuthStore()
  const navigate = useNavigate()
  const handleLogout = () => { logout(); navigate('/login') }
  const [liveStatus, setLiveStatus] = useState<Record<string, ServerLiveStatus>>({})
  const [onlineOverrides, setOnlineOverrides] = useState<Record<string, boolean>>({})
  const [lastDataOverrides, setLastDataOverrides] = useState<Record<string, string>>({})
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [search, setSearch] = useState('')

  const { data: servers = [] } = useQuery({
    queryKey: ['servers'],
    queryFn: () => serversApi.getAll().then(r => r.data),
    refetchInterval: 15000,
  })

  const onStatusUpdate = useCallback((serverId: string, status: unknown) => {
    setLiveStatus(prev => ({ ...prev, [serverId]: status as ServerLiveStatus }))
  }, [])
  const onOnlineChanged = useCallback((serverId: string, isOnline: boolean) => {
    setOnlineOverrides(prev => ({ ...prev, [serverId]: isOnline }))
  }, [])
  const onLastDataAt = useCallback((serverId: string, lastDataAt: string) => {
    setLastDataOverrides(prev => ({ ...prev, [serverId]: lastDataAt }))
  }, [])

  useServerStatusHub(onStatusUpdate, onOnlineChanged, undefined, onLastDataAt, true)

  const resolved = useMemo(() => servers.map(s =>
    onlineOverrides[s.id] !== undefined ? { ...s, isOnline: onlineOverrides[s.id] } : s
  ), [servers, onlineOverrides])

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return resolved
    return resolved.filter(s =>
      s.name.toLowerCase().includes(q) ||
      s.company?.name?.toLowerCase().includes(q) ||
      (s.ipAddress ?? '').toLowerCase().includes(q)
    )
  }, [resolved, search])

  const selected = selectedId ? resolved.find(s => s.id === selectedId) ?? null : null

  return (
    <div className="min-h-screen bg-canvas">
      <header className="h-16 px-8 flex items-center gap-3 border-b border-gray-200 bg-rail">
        <StackMark />
        <h1 className="text-[15px] font-semibold tracking-tight text-gray-900">EdgeManager 모니터</h1>
        <div className="ml-auto flex items-center gap-4">
          <ThemeToggle />
          <button onClick={handleLogout} className="text-[13px] text-gray-500 hover:text-gray-900 transition-colors">로그아웃</button>
        </div>
      </header>

      <main className="px-8 py-8 max-w-[1600px]">
        <LampBoard servers={resolved} />

        <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
          <h2 className="text-sm font-semibold text-gray-800">
            서버 <span className="tabular font-normal text-gray-500 ml-1">{filtered.length}</span>
          </h2>
          <input
            type="search"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="서버명, 회사명, IP로 찾기"
            aria-label="서버 찾기"
            className={`${inputCls} sm:w-72`}
          />
        </div>

        <div className="grid gap-3 [grid-template-columns:repeat(auto-fill,minmax(280px,1fr))]">
          {filtered.map(server => {
            const live = liveStatus[server.id]
            const status: CardStatus | undefined = live
              ? { cpuUsage: live.cpuUsage, memoryUsage: live.memoryUsage, diskUsage: live.diskUsage }
              : serverFlatStatus(server)
            const lastMachineLogAt = lastDataOverrides[server.id] ?? server.lastMachineLogAt
            return (
              <ServerCard
                key={server.id}
                server={{ ...server, lastMachineLogAt }}
                status={status}
                showCompany
                onClick={() => setSelectedId(server.id)}
              />
            )
          })}
          {filtered.length === 0 && (
            <p className="col-span-full bg-surface rounded-xl border border-gray-200 px-6 py-10 text-center text-sm text-gray-500">
              {resolved.length === 0 ? '등록된 서버가 없습니다.' : `"${search.trim()}"와 맞는 서버가 없습니다.`}
            </p>
          )}
        </div>

        {selected && <ServerDetailModal server={selected} onClose={() => setSelectedId(null)} />}
      </main>
    </div>
  )
}
